import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Alert, Autocomplete, Button, Chip, Grid, InputAdornment, Stack, TextField, Typography,
} from '@mui/material';
import { Lock } from '@mui/icons-material';
import FormDrawer from '../ui/FormDrawer';
import { FormSection, FormGrid } from '../ui/forms';
import type { MasterFormState } from './MasterFormDrawer';
import {
  getCityReference,
  getCityUsage,
  getMasters,
  type CityReference,
} from '../../services/platform/masterService';
import {
  CITY_FIELD_LABELS,
  CITY_MAX_SORT_ORDER,
  nextCitySortOrder,
  normalizeCityName,
  previewCityCode,
  validateCityForm,
  type CityExistingRecord,
} from '../../utils/cityMasterValidation';
import { countryDisplayName, countryFlag } from '../../utils/stateMasterValidation';
import { limitConsecutiveRepeatedCharacters } from '../../utils/descriptionValidation';

interface Props {
  open: boolean;
  editing: boolean;
  form: MasterFormState;
  onChange: (form: MasterFormState) => void;
  onClose: () => void;
  onSubmit: () => void;
  loading?: boolean;
  editingId?: string;
  saveError?: string | null;
}

interface CountryOption { code: string; name: string; isActive: boolean }
interface StateOption { iso: string; code: string; name: string; isActive: boolean }
type ReferenceCity = CityReference['cities'][number];

const str = (v: unknown) => (v == null ? '' : String(v).trim());

export default function CityFormDrawer({
  open,
  editing,
  form,
  onChange,
  onClose,
  onSubmit,
  loading,
  editingId,
  saveError,
}: Props) {
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const initializedFor = useRef<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const country = str(form.country).toUpperCase();
  const state = str(form.state).toUpperCase();
  const meta = form.metadata;

  const { data: countriesRaw, isLoading: countriesLoading } = useQuery({
    queryKey: ['masters', 'countries', 'state-form'],
    queryFn: () => getMasters('countries', { limit: 1000, page: 1 }),
    enabled: open,
    staleTime: 5 * 60_000,
  });
  const countryOptions = useMemo<CountryOption[]>(
    () => ((countriesRaw?.data ?? []) as Array<{ code?: string; name?: string; isActive?: boolean }>)
      .map((c) => ({ code: str(c.code).toUpperCase(), name: str(c.name), isActive: c.isActive !== false }))
      .filter((c) => /^[A-Z]{2}$/.test(c.code))
      .sort((a, b) => a.name.localeCompare(b.name)),
    [countriesRaw],
  );
  const selectedCountry = useMemo<CountryOption | null>(() => {
    if (!country) return null;
    return countryOptions.find((c) => c.code === country)
      || { code: country, name: countryDisplayName(country), isActive: true };
  }, [country, countryOptions]);

  const { data: statesRaw, isLoading: statesLoading } = useQuery({
    queryKey: ['masters', 'states', 'country', country],
    queryFn: () => getMasters('states', { country, limit: 2000, page: 1 }),
    enabled: open && !!country,
    staleTime: 0,
  });
  const stateOptions = useMemo<StateOption[]>(
    () => ((statesRaw?.data ?? []) as Array<{ code?: string; name?: string; isActive?: boolean; metadata?: Record<string, unknown> }>)
      .map((s) => {
        const code = str(s.code).toUpperCase();
        return {
          iso: str(s.metadata?.isoCode).toUpperCase() || code.replace(/^[A-Z]{2}-/, ''),
          code,
          name: str(s.name),
          isActive: s.isActive !== false,
        };
      })
      .sort((a, b) => a.name.localeCompare(b.name)),
    [statesRaw],
  );
  const selectedState = useMemo<StateOption | null>(() => {
    if (!state) return null;
    return stateOptions.find((s) => s.iso === state)
      || { iso: state, code: `${country}-${state}`, name: state, isActive: true };
  }, [state, stateOptions, country]);

  const { data: reference, isLoading: referenceLoading } = useQuery({
    queryKey: ['city-reference', country, state],
    queryFn: () => getCityReference(country, state),
    enabled: open && !!country && !!state && !editing,
    staleTime: Infinity,
  });
  const { data: siblingsRaw, isFetching: siblingsLoading } = useQuery({
    queryKey: ['masters', 'cities', 'state', country, state],
    queryFn: () => getMasters('cities', { country, state, limit: 5000, page: 1 }),
    enabled: open && !!country && !!state,
    staleTime: 0,
  });
  const siblings = useMemo<CityExistingRecord[]>(
    () => ((siblingsRaw?.data ?? []) as CityExistingRecord[]),
    [siblingsRaw],
  );
  const { data: usage } = useQuery({
    queryKey: ['city-usage', editingId],
    queryFn: () => getCityUsage(editingId!),
    enabled: open && editing && !!editingId,
    staleTime: 0,
  });

  const takenCodes = useMemo(
    () => new Set(siblings.filter((r) => r._id !== editingId).map((r) => str(r.code).toUpperCase())),
    [siblings, editingId],
  );
  const takenNames = useMemo(
    () => new Set(siblings.filter((r) => r._id !== editingId).map((r) => normalizeCityName(r.name).toLowerCase())),
    [siblings, editingId],
  );
  const referenceCities = reference?.cities ?? [];
  const selectedReference = useMemo(
    () => referenceCities.find((c) => c.name.toLowerCase() === normalizeCityName(form.name).toLowerCase()) || null,
    [referenceCities, form.name],
  );
  const suggestedSortOrder = useMemo(() => nextCitySortOrder(siblings, editingId), [siblings, editingId]);
  const effectiveCode = editing
    ? str(form.code).toUpperCase()
    : previewCityCode(country, state, form.name, takenCodes);

  useEffect(() => {
    if (!open) {
      initializedFor.current = null;
      return;
    }
    const key = editing ? `edit:${editingId}` : 'create';
    if (initializedFor.current === key) return;
    initializedFor.current = key;
    setTouched(new Set());
    if (!editing) onChange({ ...form, isActive: true, code: '', sortOrder: 0 });
  }, [open, editing, editingId, form, onChange]);

  useEffect(() => {
    if (!open || editing || initializedFor.current !== 'create') return;
    const patch: Partial<MasterFormState> = {};
    if (effectiveCode !== str(form.code)) patch.code = effectiveCode;
    if (state && !siblingsLoading && !touched.has('sortOrder') && form.sortOrder !== suggestedSortOrder) {
      patch.sortOrder = suggestedSortOrder;
    }
    if (Object.keys(patch).length) onChange({ ...form, ...patch });
  }, [open, editing, effectiveCode, state, siblingsLoading, touched, suggestedSortOrder, form, onChange]);

  const errors = useMemo(
    () => validateCityForm(
      { country, state, name: form.name, code: effectiveCode, description: form.description, sortOrder: form.sortOrder },
      { existing: siblings, editingId },
    ),
    [country, state, form.name, effectiveCode, form.description, form.sortOrder, siblings, editingId],
  );
  const blocking = Object.keys(errors).map((key) => [key, CITY_FIELD_LABELS[key] || key] as const);
  const isValid = blocking.length === 0 && !siblingsLoading;

  const touch = (...keys: string[]) => setTouched((prev) => {
    const next = new Set(prev);
    keys.forEach((k) => next.add(k));
    return next;
  });
  const errorFor = (key: string) => (touched.has(key) ? errors[key] : undefined);
  const focusField = (key: string) => {
    touch(key);
    contentRef.current?.querySelector(`[data-field="${key}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const applyCountry = (option: CountryOption | null) => {
    onChange({ ...form, country: option?.code || '', state: '', name: '', code: '', sortOrder: 0, metadata: {} });
    setTouched(new Set(['country']));
  };
  const applyState = (option: StateOption | null) => {
    onChange({ ...form, state: option?.iso || '', name: '', code: '', sortOrder: 0, metadata: {} });
    setTouched(new Set(['country', 'state']));
  };
  const applyReferenceCity = (city: ReferenceCity | null) => {
    onChange({
      ...form,
      name: city ? city.name : '',
      metadata: city ? { latitude: city.latitude ?? '', longitude: city.longitude ?? '' } : {},
    });
    touch('name');
  };

  const handleSubmit = () => {
    touch(...Object.keys(errors));
    if (!isValid) return;
    onSubmit();
  };

  const usedBy = usage && usage.total > 0
    ? [
      usage.zones ? `${usage.zones} operational zone${usage.zones === 1 ? '' : 's'}` : '',
      usage.dispatchZones ? `${usage.dispatchZones} dispatch zone${usage.dispatchZones === 1 ? '' : 's'}` : '',
      usage.addresses ? `${usage.addresses} saved address${usage.addresses === 1 ? '' : 'es'}` : '',
    ].filter(Boolean).join(', ')
    : '';
  const remaining = referenceCities.filter((c) => !takenNames.has(c.name.toLowerCase())).length;
  const actionLabel = editing ? 'Update' : 'Create';

  return (
    <FormDrawer
      open={open}
      title={`${editing ? 'Edit' : 'Add'} City`}
      subtitle="Select the country and state first, then pick a city in that state — the code, sort order and coordinates fill in automatically."
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel={actionLabel}
      loading={loading}
      disableSubmit={!isValid}
      submitHint={blocking.length
        ? `${blocking.length} item${blocking.length === 1 ? '' : 's'} to complete before you can ${actionLabel.toLowerCase()} — see the checklist at the top`
        : undefined}
      width={640}
      contentRef={contentRef}
    >
      {saveError && <Alert severity="error" sx={{ mb: 2 }}>{saveError}</Alert>}
      {blocking.length > 0 && (
        <Alert severity={state ? 'warning' : 'info'} sx={{ mb: 2 }} data-validation-summary>
          <Typography variant="body2" fontWeight={600} gutterBottom={!!state}>
            {!country
              ? 'Start by selecting a country, then the state / province.'
              : !state
                ? 'Now select the state / province — cities are listed per state.'
                : `Complete these to enable ${actionLabel}:`}
          </Typography>
          {state && (
            <Stack direction="row" flexWrap="wrap" gap={0.75}>
              {blocking.map(([key, label]) => (
                <Chip key={key} size="small" label={label} color="warning" variant="outlined" onClick={() => focusField(key)} />
              ))}
            </Stack>
          )}
        </Alert>
      )}
      {editing && (
        <Alert severity={usedBy ? 'warning' : 'info'} sx={{ mb: 2 }}>
          {usedBy
            ? `Linked to ${usedBy}. Country, state and code are locked so these links stay intact.`
            : 'Country, state and code are locked to keep this city linked to its state.'}
        </Alert>
      )}

      <FormSection title="Location" divider>
        <FormGrid>
          <Grid item xs={12} sm={6} data-field="country">
            <Autocomplete
              options={countryOptions}
              loading={countriesLoading}
              disabled={editing}
              value={selectedCountry}
              onChange={(_, value) => applyCountry(value)}
              onBlur={() => touch('country')}
              getOptionLabel={(o) => `${countryFlag(o.code)} ${o.name} (${o.code})`.trim()}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              getOptionDisabled={(o) => !o.isActive}
              filterOptions={(options, s) => {
                const q = s.inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter((o) => o.name.toLowerCase().includes(q) || o.code.toLowerCase() === q);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.code}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <span>{countryFlag(o.code)}</span>
                    <Typography variant="body2" sx={{ flex: 1 }}>{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.code}</Typography>
                    {!o.isActive && <Chip size="small" label="Inactive" />}
                  </Stack>
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="small"
                  label="Country"
                  placeholder="Search country"
                  error={!!errorFor('country')}
                  helperText={errorFor('country') || (editing ? 'Locked after creation' : 'Step 1')}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="state">
            <Autocomplete
              options={stateOptions}
              loading={statesLoading}
              disabled={editing || !country}
              value={selectedState}
              onChange={(_, value) => applyState(value)}
              onBlur={() => touch('state')}
              getOptionLabel={(o) => `${o.name} (${o.code})`}
              isOptionEqualToValue={(o, v) => o.iso === v.iso}
              getOptionDisabled={(o) => !o.isActive}
              filterOptions={(options, s) => {
                const q = s.inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter((o) => o.name.toLowerCase().includes(q)
                  || o.iso.toLowerCase() === q
                  || o.code.toLowerCase() === q);
              }}
              renderOption={(props, o) => (
                <li {...props} key={o.code}>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                    <Typography variant="body2" sx={{ flex: 1 }}>{o.name}</Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">{o.code}</Typography>
                    {!o.isActive && <Chip size="small" label="Inactive" />}
                  </Stack>
                </li>
              )}
              noOptionsText={country ? 'No states for this country — add them in States / Provinces' : 'Select a country first'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  size="small"
                  label="State / Province"
                  placeholder={country ? 'Search state' : 'Select a country first'}
                  error={!!errorFor('state')}
                  helperText={errorFor('state') || (editing ? 'Locked after creation' : 'Step 2 — only active states')}
                />
              )}
            />
          </Grid>
          {!editing && (
            <Grid item xs={12} data-field="city">
              <Autocomplete<ReferenceCity>
                options={referenceCities}
                loading={referenceLoading || siblingsLoading}
                disabled={!state}
                value={selectedReference}
                onChange={(_, value) => applyReferenceCity(value)}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(o, v) => o.name === v.name}
                getOptionDisabled={(o) => takenNames.has(o.name.toLowerCase())}
                renderOption={(props, o) => (
                  <li {...props} key={o.name}>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ width: '100%' }}>
                      <Typography variant="body2" sx={{ flex: 1 }}>{o.name}</Typography>
                      {takenNames.has(o.name.toLowerCase()) && <Chip size="small" label="Already added" />}
                    </Stack>
                  </li>
                )}
                noOptionsText={state ? 'No listed cities — type the city name below' : 'Select a state first'}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label={`Cities in ${selectedState?.name || 'the selected state'}`}
                    placeholder={state ? 'Search a city in this state' : 'Select a state first'}
                    helperText={state
                      ? referenceCities.length
                        ? `Step 3 — ${remaining} of ${referenceCities.length} listed cities not added yet · or type a new name below`
                        : 'Step 3 — type the city name below'
                      : 'Step 3 — available after you pick a state'}
                  />
                )}
              />
            </Grid>
          )}
        </FormGrid>
      </FormSection>

      <FormSection title="Details" divider>
        <FormGrid>
          <Grid item xs={12} sm={6} data-field="name">
            <TextField
              fullWidth
              size="small"
              required
              label="City name"
              value={form.name}
              disabled={!state}
              onChange={(e) => {
                onChange({ ...form, name: limitConsecutiveRepeatedCharacters(e.target.value).replace(/^\s+/, '') });
                touch('name');
              }}
              onBlur={() => {
                touch('name');
                const clean = normalizeCityName(form.name);
                if (clean !== form.name) onChange({ ...form, name: clean });
              }}
              error={!!errorFor('name')}
              helperText={errorFor('name') || 'Must be unique within the state'}
              inputProps={{ maxLength: 80 }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="code">
            <TextField
              fullWidth
              size="small"
              label="Code"
              value={effectiveCode}
              placeholder={state ? `${country}_${state}_…` : ''}
              error={!!errors.code && !!effectiveCode}
              helperText={(effectiveCode ? errors.code : undefined)
                || (editing ? 'Codes cannot be changed after creation' : 'Auto-generated from country, state and name')}
              InputProps={{
                readOnly: true,
                sx: { fontFamily: 'monospace' },
                endAdornment: (
                  <InputAdornment position="end">
                    <Chip size="small" icon={<Lock sx={{ fontSize: 14 }} />} label="Auto" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} data-field="sortOrder">
            <TextField
              fullWidth
              size="small"
              required
              type="number"
              label="Sort order"
              disabled={!state}
              value={form.sortOrder >= 1 ? form.sortOrder : ''}
              onChange={(e) => {
                const raw = e.target.value;
                if (/[-+,eE.]/.test(raw)) return;
                onChange({ ...form, sortOrder: raw === '' ? 0 : Number(raw) });
                touch('sortOrder');
              }}
              onBlur={() => touch('sortOrder')}
              error={!!errors.sortOrder && !!state && (touched.has('sortOrder') || /already used/.test(errors.sortOrder))}
              helperText={
                state && errors.sortOrder && (touched.has('sortOrder') || /already used/.test(errors.sortOrder))
                  ? errors.sortOrder
                  : state
                    ? `Unique within ${selectedState?.name || state} — next available is ${suggestedSortOrder}`
                    : 'Suggested after you pick a state'
              }
              inputProps={{ min: 1, max: CITY_MAX_SORT_ORDER, step: 1 }}
              InputProps={{
                endAdornment: state && form.sortOrder !== suggestedSortOrder && !editing ? (
                  <InputAdornment position="end">
                    <Button size="small" onClick={() => { onChange({ ...form, sortOrder: suggestedSortOrder }); touch('sortOrder'); }}>
                      Use {suggestedSortOrder}
                    </Button>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              {editing
                ? 'Use the status toggle in the Cities list to activate or deactivate this city.'
                : 'New cities are created as Active. Use the status toggle in the list to deactivate.'}
            </Typography>
          </Grid>
          <Grid item xs={12} data-field="description">
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Description"
              value={form.description}
              onChange={(e) => {
                onChange({ ...form, description: limitConsecutiveRepeatedCharacters(e.target.value) });
                touch('description');
              }}
              onBlur={() => touch('description')}
              error={!!errorFor('description')}
              helperText={errorFor('description') || `${str(form.description).length}/500 · no repeated characters or words`}
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
          {(str(meta.latitude) || str(meta.longitude)) && (
            <Grid item xs={12}>
              <Typography variant="caption" color="text.secondary">
                Coordinates: {Number(meta.latitude).toFixed(4)}, {Number(meta.longitude).toFixed(4)}
              </Typography>
            </Grid>
          )}
        </FormGrid>
      </FormSection>
    </FormDrawer>
  );
}
